import React, { useCallback, useEffect, useRef, useState } from 'react'
import { KonektoNavbar } from '../../components'
import './Conversations.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import styles from '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css'
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
} from '@chatscope/chat-ui-kit-react'
import { getConversations } from '../../store/Actions/Conversations'
import {
  CONVERSATIONS_PER_PAGE,
  conversationsService,
} from '../../services/conversationsService'
import moment from 'moment/moment'
import { decodeHtml, hasValue } from '../../helpers'
import placeholderImage from '../../assets/images/detailsPlaceholder.jpg'
import { MESSAGE_TYPE } from '../../constants/config'
import { IoChevronForward } from 'react-icons/io5'
import { GiConversation } from 'react-icons/gi'

export default function Conversations() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [selectedConversation, setSelectedConversation] = useState()
  const [messages, setMessages] = useState([]) // Messages of a selected conversation
  const messagesPage = useRef(1)
  const messagesLastPage = useRef(null)
  const isLoadingMoreMessages = useRef(false)

  const { token } = useSelector(state => state.authentication)
  const userInfo = useSelector(state => state.authentication?.userInfo)

  const conversationData = useSelector(state => state.conversations.list)
  const conversations = useSelector(
    state => state.conversations.list?.data || [],
  )
  const [conversationsList, setConversationsList] = useState([])

  const conversationsListRef = useRef(null)
  const conversationsPage = useRef(1)
  const isLoadingMoreConversations = useRef(false)

  const loadMoreConversations = async () => {
    if (
      conversationsList.length === 0 ||
      conversationsList.length < CONVERSATIONS_PER_PAGE ||
      isLoadingMoreConversations.current
    )
      return

    if (conversationData?.last_page === conversationsPage.current) return

    isLoadingMoreConversations.current = true
    conversationsPage.current += 1

    const response = await conversationsService.getConversations({
      page: conversationsPage.current,
    })

    if (response.isOk && response.data?.data?.length > 0) {
      setConversationsList(prevConversations => [
        ...prevConversations,
        ...response.data.data,
      ])
    }

    isLoadingMoreConversations.current = false
  }

  const handleConversationsScroll = () => {
    const el = conversationsListRef.current
    if (!el) return

    const isScrolledToBottom =
      el.scrollHeight - el.scrollTop <= el.clientHeight + 1 // 1 for rounding

    if (isScrolledToBottom) {
      loadMoreConversations()
    }
  }

  const onConversationClick = (conversation, isUnread) => {
    setSelectedConversation(conversation)
    if (isUnread) {
      const conversationIndex = conversationsList.findIndex(
        c => c.id === conversation.id,
      )

      const updatedList = conversationsList.map((conv, index) => {
        if (conv.id !== conversation.id) return conv

        return {
          ...conv,
          messages: [
            {
              ...conv.messages[0],
              read_at: moment(),
            },
          ],
        }
      })

      setConversationsList(updatedList)

      conversationsService.markMessageAsRead({
        messageId: conversation.messages[0].id,
      })
    }
  }

  const renderConversationItem = conversation => {
    const lastMessage = conversation.messages[0]
    const lastMessageContent = lastMessage
      ? decodeHtml(lastMessage.content)
      : ''

    const isUnread =
      lastMessage?.read_at === null && lastMessage?.user_id !== userInfo?.id

    const participant = conversation.participants?.find(
      p => p.id !== userInfo?.id,
    )

    const participantName = participant?.company
      ? participant.company.name
      : `${participant?.first_name ?? ''} ${participant?.last_name ?? ''}`

    let participantImage = placeholderImage
    if (participant?.company?.cover_image) {
      participantImage = participant.company.cover_image
    }

    const participantInitials = `${participant?.first_name?.charAt(0)}${participant?.last_name?.charAt(0)}`

    return (
      <div
        className={`c-item ${conversation.id === selectedConversation?.id ? 'c-active' : ''} ${isUnread ? 'unread' : ''}`}
        key={conversation.id}
        onClick={() => onConversationClick(conversation, isUnread)}>
        <div className="user-image-container">
          {participant.company ? (
            <img src={participantImage} />
          ) : (
            participantInitials
          )}
        </div>

        <div>
          <h6 className="truncate-line mb-0">{`${participantName}`}</h6>
          {conversation?.title ? (
            <small className="truncate-line text-muted">
              {conversation.title}
            </small>
          ) : null}
          <p className="truncate-line mb-0">{lastMessageContent}</p>
        </div>

        {isUnread ? <div className="unread-mark" /> : null}
      </div>
    )
  }

  const sendMessage = async messageText => {
    if (!hasValue(messageText) || !selectedConversation) {
      return
    }

    const response = await conversationsService.sendMessage({
      conversationId: selectedConversation.id,
      content: messageText,
    })
    if (response.isOk) {
      setMessages(prevMessages => {
        const message = {
          ...response.data,
          sender: {
            ...userInfo,
          },
        }
        return [...prevMessages, message]
      })
    }

    // Update last message in conversations list
    const conversationIndex = conversationsList.findIndex(
      c => c.id === selectedConversation.id,
    )

    const updatedList = conversationsList.map((conv, index) => {
      if (index !== conversationIndex) return conv

      const newMessages = [
        {
          ...response.data,
          read_at: moment(), // Optional if needed
        },
      ]

      return {
        ...conv,
        messages: newMessages,
      }
    })
    setConversationsList(updatedList)
  }

  const renderSelectedConversationInfo = useCallback(() => {
    if (!selectedConversation) {
      return
    }

    let link = null
    let title = null

    if (selectedConversation.type === MESSAGE_TYPE.request) {
      title = selectedConversation.title
    }

    if (selectedConversation.type === MESSAGE_TYPE.product) {
      // Participant[1] because it's the participant who created product - meaning participant[0] started conversation
      link = `/firma/${selectedConversation.participants[1].company?.id}?p=${selectedConversation.product_id}`
      title = selectedConversation.title
    }

    if (selectedConversation.type === MESSAGE_TYPE.private) {
      const participant = selectedConversation.participants?.find(
        p => p.id !== userInfo?.id,
      )
      if (participant?.company) {
        link = `/firma/${participant.company.id}`
        title = participant.company.name
      } else {
        title = `${participant.first_name ?? ''} ${participant.last_name ?? ''}`
      }
    }

    return (
      <div className="product-info">
        {link ? (
          <a className="link" target="_blank" href={link}>
            {title}
            <IoChevronForward />
          </a>
        ) : (
          <span className="link">{title}</span>
        )}
      </div>
    )
  }, [selectedConversation])

  const loadMoreMessages = async () => {
    // console.log('loadMoreMessages called')

    // PROBLEM: selectedConversation is set
    // messages.length is > 1, but for other conversation

    if (
      !selectedConversation ||
      messages.length === 0 ||
      isLoadingMoreMessages.current ||
      messagesPage.current === messagesLastPage.current
    ) {
      return
    }

    // console.log('Loading more messages...')

    isLoadingMoreMessages.current = true

    const response = await conversationsService.getMessages({
      conversationId: selectedConversation.id,
      page: messagesPage.current + 1,
    })
    if (response.isOk) {
      messagesPage.current += 1
      const newMessages = response.data.data.reverse()
      setMessages(prevMessages => [...newMessages, ...prevMessages])
    }

    isLoadingMoreMessages.current = false
  }

  useEffect(() => {
    const getMessages = async () => {
      const response = await conversationsService.getMessages({
        conversationId: selectedConversation.id,
        page: 1,
      })

      if (response.isOk) {
        messagesLastPage.current = response.data.last_page

        const reversedMessages = response.data.data.reverse()
        setMessages(reversedMessages)
      }
    }

    if (selectedConversation) {
      messagesPage.current = 1
      getMessages()
    }
  }, [selectedConversation])

  useEffect(() => {
    setConversationsList(conversations)
  }, [conversations])

  useEffect(() => {
    if (token) {
      dispatch(getConversations({ page: 1 }))
    } else {
      navigate('/prijava')
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }, 100)
  }, [token])

  if (!userInfo || !token) {
    return null
  }

  return (
    <div id="conversations">
      <KonektoNavbar />
      <div className="main-container">
        <div
          className="c-conversations"
          ref={conversationsListRef}
          onScroll={handleConversationsScroll}>
          {conversationsList.map(conversation => {
            return renderConversationItem(conversation)
          })}
          {conversationsList.length === 0 ? (
            <p className="empty-list-message">{`Lista konverzacija je prazna`}</p>
          ) : null}
        </div>

        <div className="c-messages">
          {renderSelectedConversationInfo()}
          <MainContainer>
            {selectedConversation ? (
              <ChatContainer>
                <MessageList
                  className="k-message-list"
                  onYReachStart={() => loadMoreMessages()}
                  onYReachEnd={() => console.log('on reach end')}>
                  {messages.map(message => {
                    const sender = `${message.sender.first_name ?? ''} ${message.sender.last_name ?? ''}`

                    return (
                      <Message
                        key={message.id}
                        model={{
                          message: message.content,
                          sentTime: moment(message.created_at).format(
                            'DD.MM.YYYY',
                          ),
                          sender,
                          position: 'last',
                          direction:
                            message.sender.id === userInfo.id
                              ? 'outgoing'
                              : 'incoming',
                        }}
                      />
                    )
                  })}
                </MessageList>
                <MessageInput
                  attachButton={false}
                  placeholder="Type message here"
                  onSend={sendMessage}
                />
              </ChatContainer>
            ) : (
              <div className="messages-placeholder">
                <GiConversation />
                <p>{`Odaberite konverzaciju za prikaz poruka`}</p>
              </div>
            )}
          </MainContainer>
        </div>
      </div>
    </div>
  )
}
